/**
 * Quotation Item Row Component
 *
 * Individual row for quotation items with inline editing
 *
 * @module pages/quotations/QuotationItemRow
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Form, Button, InputGroup } from 'react-bootstrap';
import { ITEM_TYPE, UNITS } from '../../utils/constants';
import { formatCurrency } from '../../utils/formatters';
import { calculateItemTotal } from '../../utils/calculations';
import PriceSourceBadge from './PriceSourceBadge';

/**
 * QuotationItemRow component
 * @param {Object} props
 * @param {Object} props.item - Item data
 * @param {number} props.index - Item index
 * @param {Function} props.onUpdate - Callback when item is updated
 * @param {Function} props.onRemove - Callback when item is removed
 * @param {Function} props.onDuplicate - Callback when item is duplicated
 * @param {boolean} props.disabled - Disable editing
 */
const QuotationItemRow = ({
  item,
  index,
  onUpdate,
  onRemove,
  onDuplicate,
  disabled = false,
}) => {
  const itemTotal = calculateItemTotal(item.quantity || 0, item.unit_price || 0);

  /**
   * Handle field change
   */
  const handleChange = (field, value) => {
    onUpdate(index, { ...item, [field]: value });
  };

  return (
    <tr>
      {/* Type */}
      <td style={{ minWidth: '120px' }}>
        <Form.Select
          size="sm"
          value={item.item_type || ''}
          onChange={(e) => handleChange('item_type', e.target.value)}
          disabled={disabled}
        >
          <option value="">Tipo...</option>
          <option value={ITEM_TYPE.MATERIAL}>Material</option>
          <option value={ITEM_TYPE.TOOL}>Herramienta</option>
          <option value={ITEM_TYPE.LABOR}>Mano de Obra</option>
          <option value={ITEM_TYPE.ADMIN}>Administración</option>
          <option value={ITEM_TYPE.OTHER}>Otro</option>
        </Form.Select>
      </td>

      {/* Description */}
      <td style={{ minWidth: '200px' }}>
        <Form.Control
          size="sm"
          type="text"
          value={item.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Descripción del artículo..."
          disabled={disabled}
        />
      </td>

      {/* Quantity — `check_quantity_positive` en la base es `quantity > 0`
          (estricto), así que el mínimo del input es 0.01, no 0 (#A7). */}
      <td style={{ width: '100px' }}>
        <Form.Control
          size="sm"
          type="number"
          min="0.01"
          step="0.01"
          value={item.quantity || ''}
          onChange={(e) => handleChange('quantity', parseFloat(e.target.value) || 0)}
          disabled={disabled}
        />
      </td>

      {/* Unit */}
      <td style={{ width: '120px' }}>
        <Form.Select
          size="sm"
          value={item.unit || ''}
          onChange={(e) => handleChange('unit', e.target.value)}
          disabled={disabled}
        >
          <option value="">Unidad...</option>
          {UNITS.map((unit) => (
            <option key={unit.value} value={unit.value}>
              {unit.label}
            </option>
          ))}
        </Form.Select>
      </td>

      {/* Unit Price */}
      <td style={{ width: '120px' }}>
        {/* #617: sin `$`. `quotation_items` no tiene columna de moneda, y esta
            empresa cotiza en colones — el simbolo decia lo contrario. */}
        <InputGroup size="sm">
          <Form.Control
            type="number"
            min="0"
            step="0.01"
            value={item.unit_price || ''}
            onChange={(e) => handleChange('unit_price', parseFloat(e.target.value) || 0)}
            disabled={disabled}
          />
        </InputGroup>
        {/* #609 — de dónde salió este número. NULL no se pinta. */}
        <PriceSourceBadge priceSource={item.price_source} index={index} />
      </td>

      {/* Total */}
      <td style={{ width: '120px' }} className="text-end">
        <strong>{formatCurrency(itemTotal)}</strong>
      </td>

      {/* Actions */}
      <td style={{ width: '120px' }}>
        <div className="d-flex gap-1">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => onDuplicate(index)}
            disabled={disabled}
            title="Duplicar"
          >
            <i className="bi bi-files"></i>
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => onRemove(index)}
            disabled={disabled}
            title="Eliminar"
          >
            <i className="bi bi-trash"></i>
          </Button>
        </div>
      </td>
    </tr>
  );
};

QuotationItemRow.propTypes = {
  item: PropTypes.shape({
    item_type: PropTypes.string,
    description: PropTypes.string,
    quantity: PropTypes.number,
    unit: PropTypes.string,
    unit_price: PropTypes.number,
    // #609 — 'stated' | 'catalog' | 'unconfirmed'; NULL = fila anterior a la
    // migración, o línea nueva sin guardar.
    price_source: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default QuotationItemRow;
